import React, { useEffect, useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';

import { Grid } from '@mui/material';
import usePublisher from 'api/usePublisher';
import { User } from 'common/contracts';
import { ELocalStorageKeys } from 'constants/enums';
import { localStorageUtil } from 'utils/localStorageUtil';
import { EToasterStatus, showToast } from 'utils/showToast';

import AcCard from 'components/AcCard/AcCard';
import AcSelect from 'components/AcSelect/AcSelect';
import Button from 'components/Button/Button';
import { EThemes } from 'components/TemplatesStudio/enums';

export const ThemeDesignsSelect: React.FC<{}> = () => {
  const { publisherId } = useParams();
  const { getPublisher, updatePublisher } = usePublisher(
    publisherId || undefined
  );

  const [value, setValue] = useState<EThemes[]>([]);

  const userDetails = localStorageUtil.getAny<User>(
    ELocalStorageKeys.USER_DETAILS
  );

  useEffect(() => {
    setValue(
      userDetails?.projects?.find(
        (project) => project.publisherId === publisherId
      )?.themes || []
    );
  }, []);

  const themesOptions = useMemo(
    () =>
      Object.values(EThemes).map((value: EThemes) => ({
        value,
        content: value
          .split('-')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' '),
        key: value
      })),
    []
  );

  const handleRenderThemesValue = (selected: any) => {
    const selectedLength = selected?.length;
    const selectedValue = themesOptions.find(
      (option: any) => option.value === selected[0]
    )?.content;
    if (
      selected &&
      selectedLength < themesOptions?.length &&
      selectedLength > 1
    )
      return selected
        .map(
          (item: string) =>
            themesOptions.find((option: any) => option.value === item)?.content
        )
        .join(', ');
    if (selectedLength === 1) return selectedValue;
    return 'All themes';
  };

  const handleUpdate = () => {
    updatePublisher.mutate(
      { themes: value },
      {
        onSuccess: async () => {
          localStorageUtil.set(ELocalStorageKeys.USER_DETAILS, {
            ...userDetails,
            projects: userDetails?.projects?.map((project) => {
              if (project.publisherId === publisherId) {
                return {
                  ...project,
                  themes: value
                };
              }
              return project;
            })
          });

          showToast({
            message: 'Themes updated successfully',
            status: EToasterStatus.SUCCESS
          });
          await getPublisher.refetch();
        },
        onError: (data: any) => {
          showToast({
            message: data.response.data.message,
            status: EToasterStatus.ERROR
          });
        }
      }
    );
  };

  return (
    <AcCard
      stackContainer={false}
      title="Store Themes"
      description="Select the themes that will be supported in the Template Studio."
      customStyles={{ marginBottom: '24px' }}
    >
      <div className="flex items-center justify-between mt-2 gap-4">
        <span className="text-xs text-black whitespace-nowrap">Themes</span>
        <div className="w-full text-xs">
          <AcSelect
            items={themesOptions}
            isCheckBox
            placeholder="none"
            isPlaceholderBold
            setSelectedItems={setValue}
            isSelectAllEnabled
            selectAllCustomLabel="All themes"
            value={value || []}
            renderValue={handleRenderThemesValue}
            isApplyBtnEnabled={false}
            selectFieldClassName={'h-[30px]'}
          />
        </div>
      </div>
      <Grid item xs={6} mt={3} mb={3}>
        <Button onClick={handleUpdate} variant="outline">
          {'Update'}
        </Button>
      </Grid>
    </AcCard>
  );
};
